/**
 * Plateau-diagram — berekend render-pad (layout_mode = "plateau-diff",
 * roadmap-tijdreizen.md §6.2). Toont een grid van ALLE diagrammen die op de
 * actieve branch leven, gekleurd t.o.v. het vorige plateau (parent-branch):
 * groen = toegevoegd, geel = gewijzigd, rood = verwijderd, neutraal =
 * ongewijzigd geërfd.
 *
 * Klik op een kaartje = SELECTEREN → detail-paneel ernaast met de element-
 * wijzigingen (objecten/relaties) die op dát diagram staan (de Gap-diff
 * gefilterd op de cel-`changedConceptIds`/`changedRelationIds`). Dubbelklik =
 * het diagram openen. Data hergebruikt de `/diff`-endpoint.
 */
import type { Diagram } from "@ae/domain";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  api,
  type BranchDiffConceptMeta,
  type BranchDiffEntity,
  type BranchDiffRelationMeta,
  type BranchDiffResponse,
  type PlateauDiagramCell,
} from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import { conceptTypeLabel } from "./gapDiffFormat";
import { ZoomPan } from "./ZoomPan";

interface PlateauDiffCanvasProps {
  diagram: Diagram;
}

type CellStatus = PlateauDiagramCell["status"];

const STATUS_CARD: Record<CellStatus, string> = {
  added: "border-green-300 bg-green-50 hover:bg-green-100",
  changed: "border-amber-300 bg-amber-50 hover:bg-amber-100",
  deleted: "border-red-300 bg-red-50 opacity-70",
  neutral: "border-slate-400 bg-white hover:bg-slate-50",
};
const STATUS_DOT: Record<CellStatus, string> = {
  added: "bg-green-500",
  changed: "bg-amber-500",
  deleted: "bg-red-500",
  neutral: "bg-slate-300",
};
const ELEMENT_DOT: Record<BranchDiffEntity["status"], string> = {
  added: "bg-green-500",
  changed: "bg-amber-500",
  deleted: "bg-red-500",
};

export function PlateauDiffCanvas({ diagram: _diagram }: PlateauDiffCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const [data, setData] = useState<BranchDiffResponse | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId || !branchId) return;
    setState("loading");
    setSelectedId(null);
    api
      .branchDiff(workspaceId, branchId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, branchId]);

  const cells = useMemo(() => data?.diagramGrid ?? [], [data]);
  const counts = useMemo(() => {
    const c = { added: 0, changed: 0, deleted: 0, neutral: 0 };
    for (const cell of cells) c[cell.status] += 1;
    return c;
  }, [cells]);

  const viewKindLabel = (viewKind: string): string => {
    if (!viewKind) return "";
    const vk = getRegistry().getViewKind(viewKind);
    return vk ? resolveBilingual(vk.name, lang) : viewKind;
  };

  const cellName = (cell: PlateauDiagramCell): string => {
    const hasName = Object.values(cell.name).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
    if (hasName) return resolveBilingual(cell.name, lang);
    return cell.label || "—";
  };

  const parentLabel = data?.parent
    ? data.parent.isMain
      ? t("gapDiff.currentSituation")
      : data.parent.name
    : null;

  const openCell = (cell: PlateauDiagramCell) => {
    if (cell.status === "deleted" || !workspaceId) return;
    useWorkspaceData.getState().openDiagram(workspaceId, cell.id);
  };

  const statusLabel: Record<CellStatus, string> = {
    added: t("plateauDiff.statusAdded"),
    changed: t("plateauDiff.statusChanged"),
    deleted: t("plateauDiff.statusDeleted"),
    neutral: t("plateauDiff.statusNeutral"),
  };

  const selectedCell = useMemo(
    () => cells.find((c) => c.logicalId === selectedId) ?? null,
    [cells, selectedId],
  );

  // Gewijzigde elementen op het geselecteerde diagram (diff gefilterd op de
  // server-berekende logicalId-sets van die cel).
  const selectedElements = useMemo(() => {
    if (!selectedCell || !data) return { concepts: [], relations: [] };
    const cSet = new Set(selectedCell.changedConceptIds);
    const rSet = new Set(selectedCell.changedRelationIds);
    return {
      concepts: data.concepts.filter((c) => cSet.has(c.logicalId)),
      relations: data.relations.filter((r) => rSet.has(r.logicalId)),
    };
  }, [selectedCell, data]);

  const conceptRow = (e: BranchDiffEntity) => {
    const m = e.meta as BranchDiffConceptMeta | undefined;
    const name = m?.name ? resolveBilingual(m.name, lang) : (e.label ?? e.logicalId);
    const type = m ? conceptTypeLabel(m.typeId, m.stereotypes, lang) : "";
    return { name: name || e.logicalId, type, status: e.status };
  };
  const relationRow = (e: BranchDiffEntity) => {
    const m = e.meta as BranchDiffRelationMeta | undefined;
    const src = m?.sourceLabel ? resolveBilingual(m.sourceLabel, lang) : "?";
    const dst = m?.targetLabel ? resolveBilingual(m.targetLabel, lang) : "?";
    const rel =
      e.label && e.label !== m?.typeId
        ? e.label
        : m
          ? conceptTypeLabel(m.typeId, m.stereotypes, lang)
          : "";
    return { name: `${src} → ${dst}`, type: rel, status: e.status };
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="border-slate-200 border-b px-4 py-2">
        <h2 className="text-center font-semibold text-slate-700 text-sm">
          {t("plateauDiff.title")}
        </h2>
        {parentLabel && (
          <p className="mt-0.5 text-center text-slate-500 text-xs">
            {t("plateauDiff.comparedTo", { plateau: parentLabel })}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-slate-200 border-b px-4 py-2 text-slate-600 text-xs">
        {(["changed", "added", "deleted", "neutral"] as const).map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT[s]}`} />
            {statusLabel[s]}: <strong>{counts[s]}</strong>
          </span>
        ))}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Grid */}
        <ZoomPan className="flex-1" contentWidth="full">
          <div className="p-4">
            {state === "loading" && (
              <p className="text-slate-500 text-sm">{t("plateauDiff.loading")}</p>
            )}
            {state === "error" && <p className="text-red-600 text-sm">{t("plateauDiff.error")}</p>}
            {state === "ready" && cells.length === 0 && (
              <p className="text-slate-500 text-sm">{t("plateauDiff.empty")}</p>
            )}
            {state === "ready" && cells.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                {cells.map((cell) => {
                  const selected = cell.logicalId === selectedId;
                  const nChanges = cell.changedConceptIds.length + cell.changedRelationIds.length;
                  return (
                    <button
                      key={cell.logicalId}
                      type="button"
                      onClick={() => setSelectedId(cell.logicalId)}
                      onDoubleClick={() => openCell(cell)}
                      title={t("plateauDiff.cardHint")}
                      className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left transition ${STATUS_CARD[cell.status]} ${selected ? "ring-2 ring-blue-400" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[cell.status]}`}
                        />
                        <span className="font-medium text-slate-900 text-sm leading-tight">
                          {cellName(cell)}
                        </span>
                      </div>
                      <span className="pl-[18px] text-slate-500 text-xs">
                        {viewKindLabel(cell.viewKind) || statusLabel[cell.status]}
                        {nChanges > 0 && (
                          <span className="ml-1 text-slate-400">
                            · {t("plateauDiff.elementChanges", { count: nChanges })}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </ZoomPan>

        {/* Detail-paneel */}
        {selectedCell && (
          <div className="flex w-72 shrink-0 flex-col overflow-hidden border-slate-200 border-l bg-slate-50">
            <div className="border-slate-200 border-b px-3 py-2">
              <div className="font-semibold text-slate-800 text-sm">{cellName(selectedCell)}</div>
              <div className="text-slate-500 text-xs">{t("plateauDiff.detailTitle")}</div>
            </div>
            <div className="flex-1 overflow-auto p-3 text-xs">
              {selectedCell.status === "deleted" ? (
                <p className="text-slate-500 italic">{t("plateauDiff.deletedDetail")}</p>
              ) : selectedElements.concepts.length === 0 &&
                selectedElements.relations.length === 0 ? (
                <p className="text-slate-500 italic">{t("plateauDiff.noElementChanges")}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {selectedElements.concepts.length > 0 && (
                    <div>
                      <div className="mb-1 font-medium text-slate-500 uppercase tracking-wide text-[10px]">
                        {t("gapDiff.tabObjects")}
                      </div>
                      <ul className="flex flex-col gap-1">
                        {selectedElements.concepts.map((e) => {
                          const r = conceptRow(e);
                          return (
                            <li key={e.logicalId} className="flex items-start gap-1.5">
                              <span
                                className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${ELEMENT_DOT[r.status]}`}
                              />
                              <span className="min-w-0">
                                <span className="font-medium text-slate-800">{r.name}</span>
                                {r.type && <span className="text-slate-400"> ({r.type})</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {selectedElements.relations.length > 0 && (
                    <div>
                      <div className="mb-1 font-medium text-slate-500 uppercase tracking-wide text-[10px]">
                        {t("gapDiff.tabRelations")}
                      </div>
                      <ul className="flex flex-col gap-1">
                        {selectedElements.relations.map((e) => {
                          const r = relationRow(e);
                          return (
                            <li key={e.logicalId} className="flex items-start gap-1.5">
                              <span
                                className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${ELEMENT_DOT[r.status]}`}
                              />
                              <span className="min-w-0">
                                <span className="font-medium text-slate-800">{r.name}</span>
                                {r.type && <span className="text-slate-400"> ({r.type})</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
